import { useEffect, useRef, useState } from 'react'
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom'
import { initConfig } from './offline/db'
import { EventsRepo } from './offline/eventsRepo'
import { LogsRepo } from './offline/logsRepo'
import { HomePage } from './pages/HomePage'
import { RegistrationGuidePage } from './pages/RegistrationGuidePage'
import { InstallPage } from './pages/InstallPage'
import { ActivationPage } from './pages/ActivationPage'
import { WaitingApprovalPage } from './pages/WaitingApprovalPage'
import { SyncService } from './services/syncService'
import { HeartbeatService } from './services/heartbeatService'
import { PermissionsService } from './services/permissionsService'
import { NetworkManager } from './services/networkManager'
import { CommandsStreamService } from './services/commandsStreamService'
import { apiClient } from './services/apiClient'
import { DeviceRegistrationService } from './services/deviceRegistrationService'
import { useDeviceStore } from './store/deviceStore'
import { UpdateNotification } from './components/UpdateNotification'
import { NetworkStatus } from './components/NetworkStatus'
import { DeviceDeletedScreen } from './components/DeviceDeletedScreen'
import './App.css'

function App() {
  const syncStopRef = useRef<(() => void) | null>(null)
  const heartbeatStopRef = useRef<(() => void) | null>(null)
  const networkCleanupRef = useRef<(() => void) | null>(null)
  const sseCleanupRef = useRef<(() => void) | null>(null)
  const cleanupIntervalRef = useRef<number | null>(null)
  const [deviceDeleted, setDeviceDeleted] = useState(false)

  useEffect(() => {
    // Проверяем флаг удаления устройства
    const deviceDeletedFlag = localStorage.getItem('device_deleted')
    if (deviceDeletedFlag === 'true') {
      console.log('[App] Device deleted flag detected')
      setDeviceDeleted(true)
      return // Не инициализируем остальное
    }

    // Инициализация конфигурации БД при загрузке приложения
    initConfig().catch((error) => {
      console.error('Failed to initialize database:', error)
    })

    // Генерируем deviceId если его нет (первый запуск)
    const initialDeviceStore = useDeviceStore.getState()
    if (!initialDeviceStore.deviceId) {
      const newDeviceId = DeviceRegistrationService.generateDeviceId()
      console.log('[App] Generated new deviceId:', newDeviceId)
      initialDeviceStore.setDeviceId(newDeviceId)
    } else {
      console.log('[App] Using existing deviceId:', initialDeviceStore.deviceId)
    }
    
    // Запрашиваем все разрешения при старте приложения
    PermissionsService.requestAllPermissions()
      .then(results => {
        console.log('[App] 📋 Permissions requested:', results)
      })
      .catch(err => {
        console.error('[App] ❌ Permission request error:', err)
      })

    // Обработчик для предотвращения ошибок IndexedDB при закрытии страницы
    // Этот обработчик дает время Service Worker'у завершить операции с IndexedDB
    const handleBeforeUnload = () => {
      // Не блокируем закрытие страницы, просто логируем
      console.log('[App] Page is unloading, IndexedDB operations should complete gracefully')
      
      // Даем Service Worker'у время завершить операции (браузер обычно дает ~2 секунды)
      // Не используем preventDefault() или returnValue, чтобы не блокировать пользователя
    }

    // Обработчик для определения когда страница становится невидимой/видимой
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        console.log('[App] Page hidden, flushing IndexedDB operations...')
        // При скрытии страницы даем время завершиться транзакциям
      } else if (document.visibilityState === 'visible') {
        console.log('[App] 👁️ Page visible, checking for updates...')
        
        // КРИТИЧНО: проверяем обновления при возврате в приложение
        // Пользователь мог отойти на долгое время, за это время могло выйти обновление
        if ('serviceWorker' in navigator) {
          navigator.serviceWorker.getRegistration().then(registration => {
            if (registration) {
              registration.update().catch(err => {
                console.error('[App] Failed to check for updates on visibility change:', err)
              })
            }
          })
        }
      }
    }

    window.addEventListener('beforeunload', handleBeforeUnload)
    document.addEventListener('visibilitychange', handleVisibilityChange)

    // Инициализируем NetworkManager для мониторинга сети
    console.log('🌐 Initializing NetworkManager...')
    networkCleanupRef.current = NetworkManager.init()

    // Подписываемся на изменения статуса сети
    const unsubscribeNetwork = NetworkManager.subscribe((status) => {
      console.log(`🌐 Network status changed: ${status}`)
      
      // При восстановлении сети запускаем форсированную синхронизацию, heartbeat и проверку обновлений
      if (status === 'online') {
        console.log('✅ Network restored, forcing sync, heartbeat and update check...')
        
        SyncService.syncNow().catch((error) => {
          console.error('Failed to force sync:', error)
        })
        
        HeartbeatService.sendHeartbeat().catch((error) => {
          console.error('Failed to force heartbeat:', error)
        })
        
        // КРИТИЧНО: проверяем обновления приложения при восстановлении сети
        // Для 24/7 приложений важно получать обновления как можно быстрее
        if ('serviceWorker' in navigator) {
          navigator.serviceWorker.getRegistration().then(registration => {
            if (registration) {
              console.log('🔄 Checking for app updates after network restore...')
              registration.update().catch(err => {
                console.error('Failed to check for updates:', err)
              })
            }
          })
        }
      }
    })

    // Настраиваем API клиент с credentials устройства
    const deviceStore = useDeviceStore.getState()
    if (deviceStore.deviceId && deviceStore.deviceSecret) {
      apiClient.setDeviceCredentials(deviceStore.deviceId, deviceStore.deviceSecret)
    }

    // Периодическая очистка IndexedDB (каждый час).
    // Это предотвращает фрагментацию SQLite, которая накапливается за неделю работы
    // и замедляет все IDB-запросы (что и вызывало "Ошибка обработки!").
    const runDbCleanup = async () => {
      try {
        // Логи: удаляем старше 24 часов
        const deletedLogs = await LogsRepo.clearOld(1)
        if (deletedLogs > 0) {
          console.log(`[App] 🧹 Log cleanup: removed ${deletedLogs} logs older than 24h`)
        }
        // События: удаляем синхронизированные события старше 1 дня.
        // Они уже на сервере. Локально нужны только для determineNextEventType
        // (последнее событие сотрудника), которое всегда свежее 24 часов.
        const deletedEvents = await EventsRepo.deleteOldSynced(1)
        if (deletedEvents > 0) {
          console.log(`[App] 🧹 Event cleanup: removed ${deletedEvents} synced events older than 1d`)
        }
      } catch (err) {
        console.warn('[App] DB cleanup failed:', err)
      }
    }
    // Сразу при старте (чистим накопленный мусор) и далее каждый час
    runDbCleanup()
    cleanupIntervalRef.current = window.setInterval(runDbCleanup, 60 * 60 * 1000)

    // Запускаем периодическую синхронизацию (каждые 30 секунд)
    // Синхронизация работает независимо от режима (scanner/admin)
    // События также синхронизируются немедленно при создании, если есть интернет
    if (deviceStore.isRegistered && deviceStore.deviceId) {
      syncStopRef.current = SyncService.startPeriodicSync(30000)
      console.log('✅ Sync service started (interval: 30s, immediate sync on event creation)')
    } else {
      console.warn('⚠️ Sync service NOT started:', {
        isRegistered: deviceStore.isRegistered,
        deviceId: deviceStore.deviceId,
        siteId: deviceStore.siteId
      })
    }

    // Запускаем SSE stream для мгновенной доставки команд (0-2 секунды)
    if (deviceStore.isRegistered && deviceStore.deviceId && deviceStore.deviceSecret) {
      sseCleanupRef.current = CommandsStreamService.connect()
      console.log('✅ SSE command stream started (instant command delivery)')
    }

    // Запускаем периодический heartbeat (каждые 30 секунд как fallback)
    // Heartbeat доставит команды если SSE соединение разорвано
    if (deviceStore.isRegistered && deviceStore.deviceId) {
      heartbeatStopRef.current = HeartbeatService.startPeriodicHeartbeat(30000)
      console.log('✅ Heartbeat service started (interval: 30s, fallback for SSE)')
    }

    // Очистка при размонтировании
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload)
      document.removeEventListener('visibilitychange', handleVisibilityChange)
      
      if (cleanupIntervalRef.current !== null) {
        clearInterval(cleanupIntervalRef.current)
      }
      if (syncStopRef.current) {
        syncStopRef.current()
        console.log('🛑 Sync service stopped')
      }
      if (sseCleanupRef.current) {
        sseCleanupRef.current()
        console.log('🛑 SSE command stream stopped')
      }
      if (heartbeatStopRef.current) {
        heartbeatStopRef.current()
        console.log('🛑 Heartbeat service stopped')
      }
      if (networkCleanupRef.current) {
        networkCleanupRef.current()
        console.log('🛑 NetworkManager stopped')
      }
      unsubscribeNetwork()
    }
  }, [])

  // Если устройство удалено, показываем только экран удаления
  if (deviceDeleted) {
    return <DeviceDeletedScreen />
  }

  return (
    <BrowserRouter>
      <UpdateNotification />
      <NetworkStatus />
      <Routes>
        {/* Установка PWA */}
        <Route path="/install" element={<InstallPage />} />
        
        {/* Активация через токен */}
        <Route path="/activate/:token" element={<ActivationPage />} />
        
        {/* Ожидание подтверждения */}
        <Route path="/waiting-approval" element={<WaitingApprovalPage />} />
        
        {/* Гайд для незарегистрированных */}
        <Route path="/registration-guide" element={<RegistrationGuidePage />} />
        
        {/* Главная страница */}
        <Route path="/" element={<HomePage />} />
        
        {/* Redirect всех неизвестных путей на главную */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </BrowserRouter>
  )
}

export default App
